/** @jsxImportSource @emotion/react */
import { UseCase } from '@app/domains/hazards/types/hazard.types'
import { layerMetadataAtom } from '@app/domains/layers/state/layers.atoms'
import { theme } from '@app/styles/theme'
import { LayerMetadataRecord } from '@app/types/layerTypes'
import { Box, Button, Typography } from '@mui/material'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DataLayerCompareHazardPanel } from './DataLayerCompareHazardPanel'
import {
  cardsContainerStyles,
  leftPanelStyles,
  pinTrayStyles,
  rightPanelStyles,
  rootStyles,
} from './HazardReference.styles'
import { HazardReferenceCard } from './HazardReferenceCard'
import { hazardLayerMetadataAtom } from '@app/domains/layers/state/layers.derived'
const MAX_PINNED = 4

/**
 * Derives unique use cases from suitability records across all layers.
 * Order is determined by first occurrence.
 */
const useUseCases = (layers: LayerMetadataRecord[]): UseCase[] =>
  useMemo(() => {
    const seen = new Map<string, string>()
    for (const layer of layers) {
      for (const s of layer.suitability) {
        if (!seen.has(s.use_case_slug)) seen.set(s.use_case_slug, s.use_case_name)
      }
    }
    return Array.from(seen.entries()).map(([slug, name]) => ({ slug, name }))
  }, [layers])

export const HazardReference = () => {
  const hazardLayers = useAtomValue(hazardLayerMetadataAtom)
  const useCases = useUseCases(hazardLayers)
  const [activeSlug, setActiveSlug] = useState<string | null>(null)
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set())
  const [dragCandidate, setDragCandidate] = useState<LayerMetadataRecord | null>(null)
  const [maxReached, setMaxReached] = useState(false)

  // Default to first use case once data loads
  useEffect(() => {
    if (useCases.length > 0 && activeSlug === null) {
      setActiveSlug(useCases[0].slug)
    }
  }, [useCases, activeSlug])

  const pinnedLayers = useMemo(
    () => hazardLayers.filter((l) => pinnedIds.has(l.id)),
    [hazardLayers, pinnedIds],
  )

  const handleTogglePin = useCallback((layer: LayerMetadataRecord) => {
    setPinnedIds((prev) => {
      const next = new Set(prev)
      next.has(layer.id) ? next.delete(layer.id) : next.size < MAX_PINNED && next.add(layer.id)
      return next
    })
  }, [])

  const handleDragStart = useCallback((layer: LayerMetadataRecord) => {
    setDragCandidate(layer)
  }, [])

  const handleDrop = useCallback(() => {
    if (!dragCandidate) return
    setPinnedIds((prev) => {
      if (prev.has(dragCandidate.id)) return prev
      if (prev.size >= MAX_PINNED) {
        setMaxReached(true)
        return prev
      }
      return new Set(prev).add(dragCandidate.id)
    })
    setDragCandidate(null)
  }, [dragCandidate])

  const handleUnpin = useCallback((layerId: string) => {
    setPinnedIds((prev) => {
      const next = new Set(prev)
      next.delete(layerId)
      return next
    })
  }, [])

  const sortedHazardLayers = useMemo(
    () =>
      [...hazardLayers].sort((a, b) => {
        const aHasTitle = a.metadata?.title != null
        const bHasTitle = b.metadata?.title != null

        if (aHasTitle && !bHasTitle) return -1
        if (!aHasTitle && bHasTitle) return 1

        if (aHasTitle && bHasTitle) {
          return a.metadata!.title.localeCompare(b.metadata!.title)
        }

        return a.id.localeCompare(b.id)
      }),
    [hazardLayers],
  )
  return (
    <Box data-testid="hazard-reference" css={rootStyles(theme)}>
      <Box css={leftPanelStyles}>
        <Box css={cardsContainerStyles}>
          {sortedHazardLayers.map((layer) => (
            <HazardReferenceCard
              key={layer.id}
              layer={layer}
              activeSlug={activeSlug}
              isPinned={pinnedIds.has(layer.id)}
              isDisabled={pinnedIds.size >= MAX_PINNED}
              onTogglePin={handleTogglePin}
              onDragStart={handleDragStart}
            />
          ))}
        </Box>
        <Box css={pinTrayStyles(theme)}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Pinned for comparison
            </Typography>
            <Typography variant="caption" fontWeight={500}>
              {pinnedIds.size} of {MAX_PINNED} max
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box css={rightPanelStyles(theme)}>
        <DataLayerCompareHazardPanel
          pinnedLayers={pinnedLayers}
          useCases={useCases}
          activeSlug={activeSlug}
          onChangeSlug={setActiveSlug}
          onDrop={handleDrop}
          onUnpin={handleUnpin}
          maxReached={maxReached}
          onDismissMax={() => setMaxReached(false)}
        />
      </Box>
    </Box>
  )
}
